import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from './Modal';
import { resolveStorageImageUrl } from '../lib/imageUrl';

export type ReceiptPreview = {
  url: string;
  title?: string;
} | null;

export const resolveReceiptPreviewUrl = (path?: string | null): string => {
  if (!path) return '';

  let url = String(path).trim();
  if (url.includes('indoteknisi.net') && !url.includes('/public/') && url.includes('/storage')) {
    url = url.replace('indoteknisi.net/storage', 'indoteknisi.net/public/storage');
  }

  return resolveStorageImageUrl(url) || url;
};

const isPdfReceipt = (url: string) => {
  const clean = url.split('?')[0].toLowerCase();
  return clean.endsWith('.pdf');
};

type ReceiptPreviewModalProps = {
  receipt: ReceiptPreview;
  onClose: () => void;
};

const ReceiptPreviewModal = ({ receipt, onClose }: ReceiptPreviewModalProps) => {
  const url = receipt ? resolveReceiptPreviewUrl(receipt.url) : '';
  const title = receipt?.title || 'Struk pengeluaran';
  const pdf = Boolean(url) && isPdfReceipt(url);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(Boolean(url));
    setHasError(false);
  }, [url]);

  return (
    <Modal
      isOpen={Boolean(url)}
      onClose={onClose}
      size="xl"
      title={title}
      contentClassName="p-0"
    >
      {url && (
        <div className="relative min-h-[320px] bg-slate-900">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
              <p className="text-sm font-semibold text-white/80">Memuat struk...</p>
            </div>
          )}

          {hasError && !isLoading && (
            <div className="flex min-h-[320px] items-center justify-center px-6 text-center text-sm font-semibold text-white/80">
              Struk gagal ditampilkan. Coba buka lagi.
            </div>
          )}

          {!hasError && pdf && (
            <iframe
              src={url}
              title={title}
              className={`h-[75vh] w-full border-0 bg-slate-100 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          )}

          {!hasError && !pdf && (
            <div className="flex max-h-[80vh] items-center justify-center p-4">
              <img
                src={url}
                alt={title}
                className={`max-h-[75vh] w-auto max-w-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ReceiptPreviewModal;
